import ExecutionEnvironment from 'exenv';
import React from 'react';
import { useSelector } from 'react-redux';
import ab from 'react-redux-hydra';
import type { IConfig, IToggle } from '@unleash/proxy-client-react';
import { FlagProvider, UnleashClient, useFlag as useFlagHook, useVariant as useVariantHook } from '@unleash/proxy-client-react';

import { track } from 'apis/amethyst';
import { evABTestEnrollment } from 'events/ab';

export type UnleashBootstrapToggles = IToggle[];
export type UnleashConfig = IConfig;

export const useFlag = useFlagHook;
export const useVariant = useVariantHook;

type Props = {
  children: React.ReactNode;
  config: UnleashConfig;
};

type UnleashEventContext = {
  sessionId: number;
  appName: string;
  environment: string;
};

interface UnleashImpressionEvent {
  eventId: string;
  context: UnleashEventContext;
  enabled: boolean;
  featureName: string;
}

// kill switch style toggle.
interface UnleashIsEnabledEvent extends UnleashImpressionEvent {
  eventType: 'isEnabled';
}

// ab test.
interface GetVariantEvent extends UnleashImpressionEvent {
  eventType: 'getVariant';
  variant: string;
}

function isVariantEvent(event: UnleashIsEnabledEvent | GetVariantEvent): event is GetVariantEvent {
  return event.eventType === 'getVariant';
}

let unleashSingleton: UnleashClient;
function startUnleashClient(config: UnleashConfig, bootstrap: UnleashBootstrapToggles) {
  const unleashClientConf = { ...config, bootstrap, context: { sessionId: ab.zfcSessionId() } };
  if (ExecutionEnvironment.canUseDOM) {
    // if its already init just return it;
    if (unleashSingleton) {
      return unleashSingleton;
    }

    unleashSingleton = new UnleashClient(unleashClientConf);
    // Only "start" the client when running client side.
    // The server side render pass will use the bootstrapped toggles
    // We don't need to record impression events in the server side render pass since by definition the client render pass will have the same impressions
    unleashSingleton.on('impression', (event: UnleashIsEnabledEvent | GetVariantEvent) => {
      // only track enabled ab test "variant" events.
      if (event.enabled && isVariantEvent(event)) {
        const abTest = {
          test: event.featureName,
          phase: 0,
          group: Number.parseInt(event.variant),
          variation: event.variant
        };
        track(() => [evABTestEnrollment, abTest]);
      }
    });
    unleashSingleton.start();
    return unleashSingleton;
  } else {
    const serverSideUnleash = new UnleashClient({ ...unleashClientConf, metricsInterval: 0, refreshInterval: 0 });
    return serverSideUnleash;
  }
}

const UnleashFlagProvider = ({ children, config }: Props) => {
  const bootstrap = useSelector(({ unleash }) => unleash.toggles);
  // configureEnvironment.js pulls config from marketplace.json if exists
  const unleashClient = startUnleashClient(config, bootstrap);

  return (
    <FlagProvider unleashClient={unleashClient} startClient={false}>
      {children}
    </FlagProvider>
  );
};

export default UnleashFlagProvider;
